import { useMutation, useQuery, useQueryClient } from "react-query";
import { OrganizationKeyName } from "../model/Organization";
import { ArchiveVoucherTypeMutationType } from "../module/voucher-type/models/VoucherType";
import { PunchCardType } from "./api-response/PunchCards";
import { http } from "./http";
import { DeliveryRule, DeliveryRuleForm } from "./api-response/DeliveryRule";
import { UploadImage } from "./api-response/UploadImage";
import {
  ChallengeType,
  DeleteChallengeType,
  UserChallenge,
} from "./api-response/Challenges";
import { UserTermListItem } from "./api-response/UserTermListSuccessResponse";
import {
  CreateHomepage,
  HomepageConfigListItem,
  UpdateHomepage,
} from "../module/shop/model/HomepageConfigListSuccessResponse";
import {
  CreateNews,
  NewsListItem,
  UpdateNews,
} from "../module/shop/model/NewsListSuccessResponse";
import { Country } from "../model/Country";
import {
  AppCallToAction,
  UpdateCallToAction,
} from "./api-response/AppCallToAction";
import { UserOrder } from "../module/shop/model/OrderSuccessResponse";
import {
  AdditionGroupItem,
  AdditionGroupPayloadCreation,
  AdditionGroupPayloadUpdate,
  ArchiveAdditionGroup,
  ShopProductAdditionsListItem,
} from "../module/shop/model/ShopProductAdditionsListSuccessResponse";
import { UserMemberCardResponseData } from "./api-response/UsersMemberCardsSuccessResponse";
import {
  UserMemberCardCreatePayload,
  UserMemberCardDeletePayload,
  UserMemberCardSyncPayload,
} from "./api-response/UserMemberCardSyncCardSuccessResponse";
import { UserProfileSuccessResponse } from "./api-response";
import { Partnership } from "./api-response/PartnershipListSuccessResponse";
import { MemberCardType } from "../module/member-card-type/models/MemberCardTypesSuccessResponse";
import { MemberCardPool } from "./api-response/MemberCardPoolsSuccessResponse";
import { VoucherTypeListItem } from "../module/shop/model/GetVoucherTypeListSuccessResponse";
import { DrinksPayCharge } from "./api-response/BeaconChargesSuccessResponse";
import { AdminUser } from "./api-response/AdminUserSuccessResponse";
import { OrganizationListItem } from "../module/organization/model/OrganizationListSuccessResponse";
import { EmailTemplateListItemResponse } from "../module/email-template/models/EmailTemplatesSuccessResponse";
import { PromotionCode } from "../module/promotion-code/model/PromotionCodeSuccessResponse";
import {
  HistoricalBalance,
  UserBalance,
} from "./api-response/UserBalanceSuccessResponse";
import { WithdrawBalance } from "../module/user/model/WithdrawBalance";
import { DepositBalance } from "../module/user/model/DepositBalance";
import { SetRole } from "../module/user/model/SetRole";
import { UserVoucherListItem } from "../module/shop/model/GetUserVoucherListSuccessResponse";
import { DeleteUserVoucher } from "../module/user/model/DeleteUserVoucher";
import { UserVoucher } from "../module/shop/model/GetUserVoucherSuccessResponse";
import { CreateUserVoucher } from "../module/user/model/CreateUserVoucher";
import { UpdateUser } from "../module/user/model/UpdateUser";
import { AnonymizeUser } from "../module/user/model/AnonymizeUser";
import { UpdatePartnership } from "../module/partnership/model/UpdatePartnership";
import { CreatePartnership } from "../module/partnership/model/CreatePartnership";
import { CreatePromotionCode } from "../module/promotion-code/model/CreatePromotionCode";
import { UpdatePromotionCode } from "../module/promotion-code/model/UpdatePromotionCode";
import { CreateEmailTemplate } from "../module/email-template/models/CreateEmailTemplate";
import { UpdateEmailTemplate } from "../module/email-template/models/UpdateEmailTemplate";
import { CreateMemberCardType } from "../module/member-card-type/models/CreateMemberCardType";
import { UpdateMemberCardType } from "../module/member-card-type/models/UpdateMemberCardType";
import { VoucherType } from "../module/shop/model/GetVoucherTypeSuccessResponse";
import { CreateVoucher } from "../module/voucher-type/models/CreateVoucher";
import { UpdateVoucher } from "../module/voucher-type/models/UpdateVoucher";

export const cacheKeys = {
  punchCards: "punchCards",
  punchCard: "punchCard",
  autoAddedProducts: "autoAddedProducts",
  deliveryRuleGroups: "delivery-rule-groups",
  deliveryRuleGroup: "delivery-rule-group",
  challenges: "challenges",
  challenge: "challenge",
  terms: "terms",
  homepages: "homepages",
  homepage: "homepage",
  newsList: "newsList",
  news: "news",
  countries: "countries",
  ctas: "ctas",
  cta: "cta",
  orders: "orders",
  order: "order",
  userOrders: "user-orders",
  additionGroups: "addition-groups",
  userMemberCards: "user-membercards",
  additionGroup: "addition-group",
  userProfile: "user-profile",
  restaurants: "restaurants",
  restaurantTags: "restaurant-tags",
  partnership: "partnership",
  partnerships: "partnerships",
  membercardTypes: "membercard-types",
  membercardType: "membercard-type",
  membercardPools: "membercard-pools",
  promotionCode: "promotion-code",
  beaconCharges: "beacon-charges",
  emailTemplate: "email-template",
  adminUser: "admin-user",
  organizations: "organizations",
  userBalance: "user-balance",
  userVouchers: "user-vouchers",
  userVoucher: "user-voucher",
  vouchers: "vouchers",
  voucher: "voucher",
  membercards: "membercards",
  providerPaymentPoints: "provider-payment-points",
  apiKeys: "api-keys",
  restaurant: "restaurant",
  historicalBalances: "historical-balances",
  userTerms: "user-terms",
};

export const usePunchCards = (organizationKeyName: OrganizationKeyName) => {
  return useQuery<PunchCardType[], any>(
    [cacheKeys.punchCards],
    () => http.getPunchCards(organizationKeyName),
    { refetchOnWindowFocus: false }
  );
};

export const usePunchCard = (
  organizationKeyName: OrganizationKeyName,
  id?: string
) => {
  return useQuery<PunchCardType | undefined, any>(
    [cacheKeys.punchCard, id],
    () => http.getPunchCard(organizationKeyName, id),
    { refetchOnWindowFocus: false, enabled: !!id }
  );
};

export const useCreatePunchCard = () => {
  const queryClient = useQueryClient();

  return useMutation<any, any, PunchCardType>(http.createPunchCard, {
    onSuccess: async () => {
      await queryClient.refetchQueries(cacheKeys.punchCards);
    },
  });
};

export const useUpdatePunchCard = () => {
  const queryClient = useQueryClient();

  return useMutation<any, any, PunchCardType>(http.updatePunchCard, {
    onSuccess: async () => {
      await queryClient.refetchQueries(cacheKeys.punchCards);
    },
  });
};

export const useAutoAddedProducts = (
  organizationKeyName: OrganizationKeyName,
  shopId?: string
) => {
  return useQuery(
    [cacheKeys.autoAddedProducts],
    () => http.getAutoAddedProducts(organizationKeyName, shopId),
    { refetchOnWindowFocus: false }
  );
};

export const useDeleteAutoAddedProduct = () => {
  const queryClient = useQueryClient();

  return useMutation(http.deleteAutoAddedProduct, {
    onSuccess: async () => {
      await queryClient.refetchQueries(cacheKeys.autoAddedProducts);
    },
  });
};

export const useCreateAutoAddedProduct = () => {
  const queryClient = useQueryClient();

  return useMutation(http.createAutoAddedProduct, {
    onSuccess: async (autoAddedProduct) => {
      await queryClient.refetchQueries(cacheKeys.autoAddedProducts);

      return autoAddedProduct;
    },
  });
};

export const useArchiveVoucherType = () => {
  return useMutation<any, any, ArchiveVoucherTypeMutationType>(
    http.archiveVoucherType
  );
};

export const useChallenges = (organizationKeyName: OrganizationKeyName) => {
  return useQuery<ChallengeType[], any>(
    [cacheKeys.challenges],
    () => http.getChallenges(organizationKeyName),
    { refetchOnWindowFocus: false }
  );
};

export const useChallenge = (
  organizationKeyName: OrganizationKeyName,
  id?: string
) => {
  return useQuery<ChallengeType | undefined, any>(
    [cacheKeys.challenge, id],
    () => http.getChallenge(organizationKeyName, id),
    { refetchOnWindowFocus: false }
  );
};

export const useCreateChallenge = () => {
  const queryClient = useQueryClient();

  return useMutation(http.createChallenge, {
    onSuccess: async () => {
      await queryClient.refetchQueries(cacheKeys.challenges);
    },
  });
};

export const useUpdateChallenge = () => {
  const queryClient = useQueryClient();

  return useMutation<any, any, ChallengeType>(http.updateChallenge, {
    onSuccess: async () => {
      await queryClient.refetchQueries(cacheKeys.challenges);
    },
  });
};

export const useDeleteChallenge = () => {
  const queryClient = useQueryClient();

  return useMutation<any, any, DeleteChallengeType>(http.deleteChallenge, {
    onSuccess: async () => {
      await queryClient.refetchQueries(cacheKeys.challenges);
    },
  });
};

export const useGetDeliveryGroups = (
  organizationKeyName: OrganizationKeyName
) => {
  return useQuery<DeliveryRule[], any>(
    [cacheKeys.deliveryRuleGroups],
    () => http.getDeliveryRuleGroups(organizationKeyName),
    { refetchOnWindowFocus: false }
  );
};

export const useGetDeliveryGroup = (
  organizationKeyName: OrganizationKeyName,
  groupId: string | undefined,
  enabled: boolean
) => {
  return useQuery<DeliveryRule | undefined, any>(
    [cacheKeys.deliveryRuleGroup, groupId],
    () => http.getDeliveryRuleGroup(organizationKeyName, groupId),
    { refetchOnWindowFocus: false, enabled }
  );
};

export const useCreateDeliveryRuleGroup = () => {
  const queryClient = useQueryClient();
  return useMutation<any, any, DeliveryRuleForm>(http.createDeliveryRuleGroup, {
    onSuccess: async () => {
      await queryClient.refetchQueries(cacheKeys.deliveryRuleGroups);
    },
  });
};

export const useUpdateDeliveryRuleGroup = (id?: string) => {
  const queryClient = useQueryClient();
  return useMutation<any, any, DeliveryRuleForm>(http.updateDeliveryRuleGroup, {
    onSuccess: async () => {
      await queryClient.refetchQueries(cacheKeys.deliveryRuleGroups);
      if (id) {
        await queryClient.refetchQueries({
          exact: true,
          queryKey: [cacheKeys.deliveryRuleGroup, id],
        });
      }
    },
  });
};

export const useUploadImage = () => {
  return useMutation<any, any, UploadImage>(http.uploadImage);
};

export const useUserChallenges = (
  organizationKeyName: OrganizationKeyName,
  userId: string
) => {
  return useQuery<UserChallenge[], any>(
    [cacheKeys.challenges, userId],
    () => http.getUserChallenges(organizationKeyName, userId),
    { refetchOnWindowFocus: false }
  );
};

export const useGetOrganizationTerms = (
  organizationKeyName?: OrganizationKeyName
) => {
  return useQuery<UserTermListItem[], any>(
    [cacheKeys.terms, organizationKeyName],
    () => http.getOrganizationUserTerms(organizationKeyName),
    { refetchOnWindowFocus: false, enabled: !!organizationKeyName }
  );
};

export const useGetHomepages = (organizationKeyName: OrganizationKeyName) => {
  return useQuery<HomepageConfigListItem[], any>(
    [cacheKeys.homepages],
    () => http.getHomepages(organizationKeyName),
    { refetchOnWindowFocus: false }
  );
};

export const useGetHomepage = (
  organizationKeyName: OrganizationKeyName,
  id?: string
) => {
  return useQuery<HomepageConfigListItem | undefined, any>(
    [cacheKeys.homepage, id],
    () => http.getHomepage(organizationKeyName, id),
    { refetchOnWindowFocus: false }
  );
};

export const useGetNewsList = (organizationKeyName: OrganizationKeyName) => {
  return useQuery<NewsListItem[], any>(
    [cacheKeys.newsList],
    () => http.getNewsList(organizationKeyName),
    { refetchOnWindowFocus: false }
  );
};

export const useGetNews = (
  organizationKeyName: OrganizationKeyName,
  id?: string
) => {
  return useQuery<NewsListItem | undefined, any>(
    [cacheKeys.news, id],
    () => http.getNews(organizationKeyName, id),
    { refetchOnWindowFocus: false, enabled: !!id }
  );
};

export const useCreateNews = () => {
  const queryClient = useQueryClient();
  return useMutation<any, any, CreateNews>(http.createNews, {
    onSuccess: async () => {
      await queryClient.refetchQueries(cacheKeys.newsList);
    },
  });
};

export const useDeleteNews = () => {
  const queryClient = useQueryClient();
  return useMutation(http.deleteNews, {
    onSuccess: async () => {
      await queryClient.refetchQueries(cacheKeys.newsList);
    },
  });
};

export const useUpdateNews = () => {
  const queryClient = useQueryClient();
  return useMutation<any, any, UpdateNews>(http.updateNews, {
    onSuccess: async () => {
      await queryClient.refetchQueries(cacheKeys.newsList);
    },
  });
};

export const useUploadNewsImage = () => {
  return useMutation<any, any, UploadImage>(http.uploadNewsImage);
};

export const useUploadHomepageImage = () => {
  return useMutation<any, any, UploadImage>(http.uploadHomepageImage);
};

export const useCreateHomepage = () => {
  const queryClient = useQueryClient();
  return useMutation<any, any, CreateHomepage>(http.createHomepage, {
    onSuccess: async () => {
      await queryClient.refetchQueries(cacheKeys.homepages);
    },
  });
};

export const useUpdateHomepage = () => {
  const queryClient = useQueryClient();
  return useMutation<any, any, UpdateHomepage>(http.updateHomepage, {
    onSuccess: async () => {
      await queryClient.refetchQueries(cacheKeys.homepages);
    },
  });
};

export const useGetCountries = (enabled: boolean = true) => {
  return useQuery<Country[] | undefined, any>(
    [cacheKeys.countries],
    () => http.getCountries(),
    // Caching in 10 minutes. Maybe we can cache it longer?
    // Data will be considered stale after 10 minutes
    // Data is cached for 10 minutes
    {
      refetchOnWindowFocus: false,
      enabled,
      staleTime: 600000,
      cacheTime: 600000,
    }
  );
};

export const useGetAppCtas = (organizationKeyName: OrganizationKeyName) => {
  return useQuery<AppCallToAction[] | undefined, any>(
    [cacheKeys.ctas],
    () => http.getAppCallToActions(organizationKeyName),
    { refetchOnWindowFocus: false }
  );
};

export const useGetAppCta = (
  organizationKeyName: OrganizationKeyName,
  id?: string
) => {
  return useQuery<AppCallToAction | undefined, any>(
    [cacheKeys.cta, id],
    () => http.getAppCallToAction(organizationKeyName, id),
    { refetchOnWindowFocus: false, enabled: !!id }
  );
};

export const useCreateCta = (organizationKeyName: OrganizationKeyName) => {
  const queryClient = useQueryClient();
  return useMutation(http.createCta, {
    onSuccess: async () => {
      await queryClient.refetchQueries(cacheKeys.ctas);
    },
  });
};

export const useUpdateCta = (organizationKeyName: OrganizationKeyName) => {
  const queryClient = useQueryClient();
  return useMutation<any, any, UpdateCallToAction>(http.updateCta, {
    onSuccess: async () => {
      await queryClient.refetchQueries(cacheKeys.ctas);
    },
  });
};

export const useDeleteCta = () => {
  const queryClient = useQueryClient();
  return useMutation(http.deleteCta, {
    onSuccess: async () => {
      await queryClient.refetchQueries(cacheKeys.ctas);
    },
  });
};

export const useGetUserOrders = (
  organizationKeyName: OrganizationKeyName,
  userId: string
) => {
  return useQuery<UserOrder[] | undefined, any>(
    [cacheKeys.userOrders, userId],
    () => http.getUserOrders(userId, organizationKeyName),
    { refetchOnWindowFocus: false }
  );
};

export const useGetAdditionGroups = (
  organizationKeyName: OrganizationKeyName
) => {
  return useQuery<ShopProductAdditionsListItem[] | undefined, any>(
    [cacheKeys.additionGroups],
    () => http.getAdditionGroups(organizationKeyName),
    { refetchOnWindowFocus: false }
  );
};

export const useGetAdditionGroup = (
  organizationKeyName: OrganizationKeyName,
  id: string | undefined
) => {
  return useQuery<AdditionGroupItem | undefined, any>(
    [cacheKeys.additionGroup, id],
    () => http.getAdditionGroup(organizationKeyName, id),
    { refetchOnWindowFocus: false, enabled: !!id }
  );
};

export const useCreateAdditionGroup = () => {
  const queryClient = useQueryClient();
  return useMutation<any, any, AdditionGroupPayloadCreation>(
    http.createAdditionGroup,
    {
      onSuccess: async () => {
        await queryClient.refetchQueries(cacheKeys.additionGroups);
      },
    }
  );
};

export const useUpdateAdditionGroup = (id?: string) => {
  const queryClient = useQueryClient();
  return useMutation<any, any, AdditionGroupPayloadUpdate>(
    http.updateAdditionGroup,
    {
      onSuccess: async () => {
        await queryClient.refetchQueries(cacheKeys.additionGroups);
        if (id) {
          await queryClient.refetchQueries({
            exact: true,
            queryKey: [cacheKeys.additionGroup, id],
          });
        }
      },
    }
  );
};

export const useArchiveAdditionGroup = () => {
  const queryClient = useQueryClient();
  return useMutation<any, any, ArchiveAdditionGroup>(
    http.archiveAdditionGroup,
    {
      onSuccess: async () => {
        await queryClient.refetchQueries(cacheKeys.additionGroups);
      },
    }
  );
};

export const useGetUserMemberCards = (
  organizationKeyName: OrganizationKeyName,
  userId?: string
) => {
  return useQuery<UserMemberCardResponseData[], any>(
    [cacheKeys.userMemberCards, userId],
    () => http.getUserMembercards(organizationKeyName, userId),
    { refetchOnWindowFocus: false, enabled: !!userId }
  );
};

export const useSyncUserMembercardStatus = (userId?: string) => {
  const queryClient = useQueryClient();
  return useMutation<any, any, UserMemberCardSyncPayload>(
    http.syncUserMembercardStatus,
    {
      onSuccess: async () => {
        queryClient.refetchQueries([cacheKeys.userMemberCards, userId]);
      },
    }
  );
};

export const useCreateUserMembercard = (userId?: string) => {
  const queryClient = useQueryClient();
  return useMutation<any, any, UserMemberCardCreatePayload>(
    http.createUserMembercard,
    {
      onSuccess: async () => {
        queryClient.refetchQueries([cacheKeys.userMemberCards, userId]);
      },
    }
  );
};

export const useDeleteUserMembercard = (userId?: string) => {
  const queryClient = useQueryClient();
  return useMutation<any, any, UserMemberCardDeletePayload>(
    http.deleteUserMembercard,
    {
      onSuccess: async () => {
        queryClient.refetchQueries([cacheKeys.userMemberCards, userId]);
      },
    }
  );
};

export const useGetUserProfile = (
  organizationKeyName: OrganizationKeyName,
  id?: string | number
) => {
  return useQuery<UserProfileSuccessResponse, any>(
    [cacheKeys.userProfile, id],
    () => http.getUserProfile(organizationKeyName, id),
    { refetchOnWindowFocus: false, enabled: !!id }
  );
};

export const useGetPartnership = (
  organizationKeyName: OrganizationKeyName,
  enabled: boolean,
  id?: string
) => {
  return useQuery<Partnership, any>(
    [cacheKeys.partnership, id],
    () => http.getPartnership(organizationKeyName, id),
    { refetchOnWindowFocus: false, enabled }
  );
};

export const useUpdatePartnership = (id?: string) => {
  const queryClient = useQueryClient();
  return useMutation<any, any, UpdatePartnership>(http.updatePartnership, {
    onSuccess: async () => {
      if (id) {
        queryClient.refetchQueries([cacheKeys.partnership, id]);
      }
    },
  });
};

export const useCreatePartnership = () => {
  return useMutation<any, any, CreatePartnership>(http.createPartnership);
};

export const useGetUserBalances = (
  organizationKeyName: OrganizationKeyName,
  id?: string | number
) => {
  return useQuery<UserBalance[] | null, any>(
    [cacheKeys.userBalance, id],
    () => http.getUserProfileBalances(organizationKeyName, id),
    { refetchOnWindowFocus: false }
  );
};

export const useGetUserHistoricalBalances = (
  organizationKeyName: OrganizationKeyName,
  countryCode: string,
  enabled: boolean = true,
  id?: string | number
) => {
  return useQuery<HistoricalBalance[], any>(
    [cacheKeys.historicalBalances, id],
    () =>
      http.getUserProfileHistoricalBalances(
        organizationKeyName,
        countryCode,
        id
      ),
    { refetchOnWindowFocus: false, enabled }
  );
};

export const useGetUserVouchers = (
  organizationKeyName: OrganizationKeyName,
  filters: any,
  id?: string | number
) => {
  return useQuery<UserVoucherListItem[], any>(
    [cacheKeys.userVouchers, id, filters],
    () => http.getUserVouchers(organizationKeyName, filters, id),
    { refetchOnWindowFocus: false }
  );
};

export const useGetUserVoucher = (
  organizationKeyName: OrganizationKeyName,
  enabled: boolean,
  id?: string
) => {
  return useQuery<UserVoucher, any>(
    [cacheKeys.userVoucher, id],
    () => http.getUserVoucher(organizationKeyName, id),
    { refetchOnWindowFocus: false, enabled }
  );
};

export const useGetMembercardTypes = (
  organizationKeyName: OrganizationKeyName
) => {
  return useQuery<MemberCardType[], any>(
    [cacheKeys.membercardTypes],
    () => http.getMembercardTypes(organizationKeyName),
    { refetchOnWindowFocus: false }
  );
};

export const useGetMembercardType = (
  organizationKeyName: OrganizationKeyName,
  id?: string
) => {
  return useQuery<MemberCardType, any>(
    [cacheKeys.membercardType, id],
    () => http.getMembercardType(organizationKeyName, id),
    { refetchOnWindowFocus: false, enabled: !!id }
  );
};

export const useCreateMembercardType = () => {
  const queryClient = useQueryClient();
  return useMutation<any, any, CreateMemberCardType>(
    http.createMembercardType,
    {
      onSuccess: async () => {
        await queryClient.refetchQueries([cacheKeys.membercardTypes]);
      },
    }
  );
};

export const useUpdateMembercardType = (id?: string) => {
  const queryClient = useQueryClient();
  return useMutation<any, any, UpdateMemberCardType>(
    http.updateMembercardType,
    {
      onSuccess: async () => {
        await queryClient.refetchQueries([cacheKeys.membercardTypes]);
        await queryClient.refetchQueries([cacheKeys.membercardType, id]);
      },
    }
  );
};

export const useUploadMembercardImage = () => {
  return useMutation<any, any, UploadImage & { type: "background" | "logo" }>(
    http.uploadMembercardTypeImage
  );
};

export const useUploadCallToActionImage = () => {
  return useMutation(http.uploadCallToActionImage);
};

export const useDeleteUserVoucher = () => {
  const queryClient = useQueryClient();
  return useMutation<any, any, DeleteUserVoucher>(http.deleteUserVoucher, {
    onSuccess: async () => {
      queryClient.refetchQueries([cacheKeys.userVouchers]);
    },
  });
};

export const useWithdrawBalance = (id?: string | number) => {
  const queryClient = useQueryClient();
  return useMutation<any, any, WithdrawBalance>(http.withdrawUserBalance, {
    onSuccess: async () => {
      queryClient.refetchQueries([cacheKeys.userBalance, id]);
    },
  });
};

export const useDepositBalance = (id?: string | number) => {
  const queryClient = useQueryClient();
  return useMutation<any, any, DepositBalance>(http.depositUserBalance, {
    onSuccess: async () => {
      queryClient.refetchQueries([cacheKeys.userBalance, id]);
    },
  });
};

export const useSetUserRoles = (id?: string | number) => {
  const queryClient = useQueryClient();
  return useMutation<any, any, SetRole>(http.setUserRoles, {
    onSuccess: async () => {
      queryClient.refetchQueries([cacheKeys.userProfile, id]);
    },
  });
};

export const useGetVouchers = (organizationKeyName: OrganizationKeyName) => {
  return useQuery<VoucherTypeListItem[], any>(
    [cacheKeys.vouchers],
    () => http.getVouchers(organizationKeyName),
    {
      refetchOnWindowFocus: false,
    }
  );
};

export const useGetVoucher = (
  organizationKeyName: OrganizationKeyName,
  id?: string
) => {
  return useQuery<VoucherType, any>(
    [cacheKeys.voucher, id],
    () => http.getVoucher(organizationKeyName, id),
    { refetchOnWindowFocus: false, enabled: !!id }
  );
};

export const useCreateVoucher = () => {
  const queryClient = useQueryClient();
  return useMutation<any, any, CreateVoucher>(http.createVoucher, {
    onSuccess: async () => {
      queryClient.refetchQueries([cacheKeys.vouchers]);
    },
  });
};

export const useUpdateVoucher = (id?: string) => {
  const queryClient = useQueryClient();
  return useMutation<any, any, UpdateVoucher>(http.updateVoucher, {
    onSuccess: async () => {
      queryClient.refetchQueries([cacheKeys.vouchers]);
      queryClient.refetchQueries([cacheKeys.voucher, id]);
    },
  });
};

export const useUploadVoucherImage = (id?: string) => {
  const queryClient = useQueryClient();
  return useMutation<any, any, UploadImage>(http.uploadVoucherTypeImage, {
    onSuccess: async () => {
      if (id) {
        await queryClient.refetchQueries([cacheKeys.voucher, id]);
      }
    },
  });
};

export const useGetMembercardPools = (
  organizationKeyName: OrganizationKeyName
) => {
  return useQuery<MemberCardPool[], any>(
    [cacheKeys.membercardPools],
    () => http.getMembercardPools(organizationKeyName),
    { refetchOnWindowFocus: false }
  );
};

export const useGetPartnerships = (
  organizationKeyName: OrganizationKeyName
) => {
  return useQuery<Partnership[], any>(
    [cacheKeys.partnerships],
    () => http.getPartnerships(organizationKeyName),
    { refetchOnWindowFocus: false }
  );
};

export const useGetPromotionCode = (
  organizationKeyName: OrganizationKeyName,
  enabled: boolean,
  id?: string
) => {
  return useQuery<PromotionCode, any>(
    [cacheKeys.promotionCode, id],
    () => http.getPromotionCode(organizationKeyName, id),
    { refetchOnWindowFocus: false, enabled }
  );
};

export const useGetBeaconCharges = (
  organizationKeyName: OrganizationKeyName,
  idNfc: string | undefined,
  filters: { [i: string]: any },
  id: string | undefined
) => {
  return useQuery<DrinksPayCharge[], any>(
    [cacheKeys.beaconCharges, id, idNfc, filters],
    () => http.getBeaconCharges(organizationKeyName, idNfc, filters, id),
    { refetchOnWindowFocus: false, enabled: !!id }
  );
};

export const useGetEmailTemplate = (
  organizationKeyName: OrganizationKeyName,
  id?: string
) => {
  return useQuery<EmailTemplateListItemResponse, any>(
    [cacheKeys.emailTemplate, id],
    () => http.getEmailTemplate(organizationKeyName, id),
    { refetchOnWindowFocus: false, enabled: !!id }
  );
};

export const useGetAdminUser = (
  organizationKeyName: OrganizationKeyName,
  id?: string
) => {
  return useQuery<AdminUser, any>(
    [cacheKeys.adminUser, id],
    () => http.getAdminUser(organizationKeyName, id),
    { refetchOnWindowFocus: false, enabled: !!id }
  );
};

export const useGetOrganizations = (
  organizationKeyName: OrganizationKeyName
) => {
  return useQuery<OrganizationListItem[], any>(
    [cacheKeys.organizations],
    () => http.getOrganizations(organizationKeyName),
    { refetchOnWindowFocus: false }
  );
};

export const useCreateEmailTemplate = () => {
  return useMutation<any, any, CreateEmailTemplate>(http.createEmailTemplate);
};

export const useUpdateEmailTemplate = (id?: string) => {
  const queryClient = useQueryClient();
  return useMutation<any, any, UpdateEmailTemplate>(http.updateEmailTemplate, {
    onSuccess: async () => {
      queryClient.refetchQueries([cacheKeys.emailTemplate, id]);
    },
  });
};

export const useCreatePromotionCode = () => {
  return useMutation<any, any, CreatePromotionCode>(http.createPromotionCode);
};

export const useUpdatePromotionCode = (id?: string) => {
  const queryClient = useQueryClient();
  return useMutation<any, any, UpdatePromotionCode>(http.updatePromotionCode, {
    onSuccess: async () => {
      queryClient.refetchQueries([cacheKeys.promotionCode, id]);
    },
  });
};

export const useCreateUserVoucher = (id?: string | number) => {
  const queryClient = useQueryClient();
  return useMutation<any, any, CreateUserVoucher>(http.createUserVoucher, {
    onSuccess: async () => {
      queryClient.refetchQueries([cacheKeys.userVouchers, id]);
    },
  });
};

export const useUpdateUser = (id?: string | number) => {
  const queryClient = useQueryClient();
  return useMutation<any, any, UpdateUser>(http.updateUser, {
    onSuccess: async () => {
      queryClient.refetchQueries([cacheKeys.userProfile, id]);
    },
  });
};
export const useUpdateUserTermConsent = ({
  id,
  organizationKeyName,
}: {
  id?: string | number;
  organizationKeyName: OrganizationKeyName;
}) => {
  const queryClient = useQueryClient();

  return useMutation(http.updateUserTermConsent, {
    onSuccess: async () => {
      queryClient.refetchQueries([cacheKeys.userProfile, id]);
      queryClient.refetchQueries([cacheKeys.userTerms, organizationKeyName]);
    },
  });
};

export const useAnonymizeUser = () => {
  return useMutation<any, any, AnonymizeUser>(http.anynomizeUser);
};

export const useUserTerms = ({
  organizationKeyName,
  enabled = true,
}: {
  organizationKeyName: OrganizationKeyName;
  enabled?: boolean;
}) => {
  return useQuery(
    [cacheKeys.userTerms, organizationKeyName],
    () => http.getUserTerms({ organizationKeyName }),
    { refetchOnWindowFocus: false, enabled }
  );
};
